/**
 * Stock math (PRD v2 §6.1). Pure, so the rules that decide what a shop believes it owns
 * are testable without a database.
 *
 * Quantities travel as INTEGER HUNDREDTHS everywhere inside this module. Postgres stores
 * `numeric(12,2)` and hands back a string; the service converts once at the boundary. Two
 * halves of a case of fittings must add back to a case, and floats don't promise that.
 */

/** "12.50" (or 12.5) → 1250. The one place quantity crosses from decimal to exact. */
export function qtyToHundredths(value: string | number): number {
  const n = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(n)) return 0;
  return Math.round(n * 100);
}

/** The most of one part a job's parts list plans: `job_parts.qty` is numeric(10,2). */
export const PLAN_QTY_MAX_HUNDREDTHS = 999_999;

/**
 * Why a planned quantity (in hundredths) is refused, or null when it is fine: more than nothing
 * and at most 9,999.99 (ops insights Phase 6a, spec §2). The server refuses with this sentence.
 */
export function planQtyProblem(hundredths: number): string | null {
  if (!Number.isFinite(hundredths) || hundredths <= 0) {
    return 'Plan at least 0.01 of a part. To drop it from the list, remove it.';
  }
  if (hundredths > PLAN_QTY_MAX_HUNDREDTHS) {
    return 'Plan at most 9,999.99 of a part.';
  }
  return null;
}

/** 1250 → "12.5" — trailing zeros dropped, because "12.50 ea" reads like money. */
export function formatQty(hundredths: number): string {
  const n = hundredths / 100;
  return Number.isInteger(n) ? String(n) : String(Number(n.toFixed(2)));
}

export interface LocationNode {
  id: string;
  parentId: string | null;
}

export interface StockSum {
  itemId: string;
  locationId: string;
  /** Movements booked AT this node, excluding descendants. */
  ownHundredths: number;
}

export interface StockCell extends StockSum {
  /** This node plus every descendant — what "how many are at the shop" actually means. */
  totalHundredths: number;
}

/**
 * Depth of each node, and its chain of ancestors. Guards against a cycle (a location
 * re-parented under its own child) by refusing to walk past the node count — a corrupt
 * tree must not hang the inventory page.
 *
 * Only through `locations` themselves: a parent that is not in the list (deleted, or another
 * shop's, as a caller that reads one shop's live places leaves out) ends the chain, so nothing
 * rolls up into a place the caller never read (p7-09 r2).
 */
function ancestorsOf(
  locations: LocationNode[],
): Map<string, { ancestors: string[]; depth: number }> {
  const parent = new Map(locations.map((l) => [l.id, l.parentId]));
  const out = new Map<string, { ancestors: string[]; depth: number }>();
  for (const node of locations) {
    const ancestors: string[] = [];
    const seen = new Set<string>([node.id]);
    let cursor = parent.get(node.id) ?? null;
    while (
      cursor &&
      parent.has(cursor) &&
      !seen.has(cursor) &&
      ancestors.length <= locations.length
    ) {
      ancestors.push(cursor);
      seen.add(cursor);
      cursor = parent.get(cursor) ?? null;
    }
    out.set(node.id, { ancestors, depth: ancestors.length });
  }
  return out;
}

/**
 * Roll each location's own quantity up into its ancestors. "How many at the shop" has to
 * include what's in the bins inside it, or every parent reads zero and the page lies.
 *
 * Returns a cell for every (item, location) pair that has either own stock or stock
 * beneath it — never a dense grid, which for 400 items × 8 locations would be 3,200 rows
 * of mostly nothing.
 */
export function rollUpStock(locations: LocationNode[], sums: StockSum[]): StockCell[] {
  const chains = ancestorsOf(locations);
  const totals = new Map<string, number>(); // `${itemId}|${locationId}` → hundredths
  const owns = new Map<string, number>();

  const key = (itemId: string, locationId: string) => `${itemId}|${locationId}`;

  for (const s of sums) {
    if (s.ownHundredths === 0) continue;
    const own = key(s.itemId, s.locationId);
    owns.set(own, (owns.get(own) ?? 0) + s.ownHundredths);
    totals.set(own, (totals.get(own) ?? 0) + s.ownHundredths);
    for (const ancestor of chains.get(s.locationId)?.ancestors ?? []) {
      const k = key(s.itemId, ancestor);
      totals.set(k, (totals.get(k) ?? 0) + s.ownHundredths);
    }
  }

  return [...totals.entries()].map(([k, totalHundredths]) => {
    const [itemId, locationId] = k.split('|') as [string, string];
    return {
      itemId,
      locationId,
      ownHundredths: owns.get(k) ?? 0,
      totalHundredths,
    };
  });
}

/**
 * "The shop" (ops insights Phase 6a, ruling R2): every ACTIVE location that belongs to no tech
 * (`userId` null) and does not sit under one that does. A bin inside a van is the van's stock,
 * whoever the bin names. Deleted locations are the caller's to leave out (they are not in the
 * tree it reads).
 *
 * The shop's stock is the sum of these locations' OWN balances, never their rolled-up totals: a
 * warehouse and the bin inside it are both shop, and a total would count the bin twice.
 */
export function shopLocationIds(
  locations: (LocationNode & { userId: string | null; active: boolean })[],
): Set<string> {
  const chains = ancestorsOf(locations);
  const tech = new Set(locations.filter((l) => l.userId !== null).map((l) => l.id));
  return new Set(
    locations
      .filter(
        (l) =>
          l.active &&
          l.userId === null &&
          !(chains.get(l.id)?.ancestors ?? []).some((a) => tech.has(a)),
      )
      .map((l) => l.id),
  );
}

/**
 * Flatten the tree into display order — each parent immediately followed by its children,
 * with a depth for indenting. Sorted by name within a level so the list doesn't reshuffle
 * when someone adds a van.
 *
 * Orphans (a parent that was archived out from under them) are surfaced at the root rather
 * than silently dropped: a location holding stock must never vanish from the page.
 */
export function orderLocationTree<T extends LocationNode & { name: string }>(
  locations: T[],
): (T & { depth: number })[] {
  const byParent = new Map<string | null, T[]>();
  const ids = new Set(locations.map((l) => l.id));
  for (const l of locations) {
    const key = l.parentId && ids.has(l.parentId) ? l.parentId : null;
    byParent.set(key, [...(byParent.get(key) ?? []), l]);
  }
  for (const list of byParent.values()) list.sort((a, b) => a.name.localeCompare(b.name));

  const out: (T & { depth: number })[] = [];
  const walk = (parentId: string | null, depth: number) => {
    for (const node of byParent.get(parentId) ?? []) {
      if (out.some((o) => o.id === node.id)) continue; // cycle guard
      out.push({ ...node, depth });
      walk(node.id, depth + 1);
    }
  };
  walk(null, 0);
  return out;
}

/**
 * A count says "there are 8 here". The ledger only speaks in deltas, so the correction is
 * the difference — and a count that matches writes nothing at all, because a ledger entry
 * that changes nothing is noise in the one record that has to explain every balance.
 */
export function countDelta(onHandHundredths: number, countedHundredths: number): number {
  return countedHundredths - onHandHundredths;
}

/**
 * How short a place is once what's already ordered is counted (§6.5). Counting on-order is
 * the whole point: a threshold that ignores the truck already on its way nags every day
 * until it arrives, and a list that cries wolf stops being read.
 *
 * Zero means "not short" — never a negative, because "we have 4 more than we need" is not a
 * replenishment quantity.
 */
export function shortfallHundredths(
  onHandHundredths: number,
  onOrderHundredths: number,
  reorderPointHundredths: number,
): number {
  return Math.max(0, reorderPointHundredths - (onHandHundredths + onOrderHundredths));
}

/**
 * What to actually put on the order. `reorderQty` is a PACK SIZE, not a fixed quantity: a
 * shop that buys capacitors in tens and is thirty short needs thirty, not ten — so the
 * shortfall rounds UP to the next multiple. Without a pack size, order exactly the gap.
 */
export function suggestedOrderHundredths(
  shortfall: number,
  reorderQtyHundredths: number | null,
): number {
  if (shortfall <= 0) return 0;
  if (!reorderQtyHundredths || reorderQtyHundredths <= 0) return shortfall;
  return Math.ceil(shortfall / reorderQtyHundredths) * reorderQtyHundredths;
}

/**
 * Moving average at receipt (§6.4, ERPNext's formula). Receiving 10 @ $4 into 5 @ $10
 * gives 15 @ $6 — the cost every job margin will consume at afterwards.
 *
 * Two cases refuse to average and take the new cost outright: no prior stock, and a
 * non-positive balance (a negative on-hand has no meaningful average to blend with).
 */
export function movingAverageCents(
  onHandHundredths: number,
  currentAvgCents: number | null,
  receivedHundredths: number,
  receivedUnitCostCents: number,
): number {
  if (receivedHundredths <= 0) return currentAvgCents ?? receivedUnitCostCents;
  if (onHandHundredths <= 0 || currentAvgCents === null) return receivedUnitCostCents;
  const existingValue = onHandHundredths * currentAvgCents;
  const incomingValue = receivedHundredths * receivedUnitCostCents;
  return Math.round(
    (existingValue + incomingValue) / (onHandHundredths + receivedHundredths),
  );
}
